import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  MapPin,
  Save,
  Send,
  Tag,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  useCreateProgramMutation,
  useGetProgramEditorQuery,
  usePublishProgramMutation,
  useUpdateProgramMutation,
} from '../../../state/domainRouterApi';
import { readDomainRouterErrorMessage } from '../../../state/domainRouterError';
import { TailwindDateField } from '../../../components/forms/TailwindDateField';
import { ModuleSectionNav } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { mapCampProgramEditorResponse } from '../camps/viewModels';
import { SCHOOL_CAMPS_SECTION_ITEMS } from './sectionNav';

/* ── Colour tokens ──────────────────────────────────────────────────── */
const PRIMARY = 'var(--primary, #2697a2)';
const BORDER = 'var(--border, #e5e7eb)';
const TEXT = 'var(--iron-grey, #404f4d)';
const TEXT_SECONDARY = 'var(--text-secondary, #6b7280)';
const AQUAMARINE = 'var(--aquamarine, #28E2A7)';
const CORAL = 'var(--coral, #FF6B6B)';

/* ── Types ──────────────────────────────────────────────────────────── */
type EditorDefaults = {
  campTypes?: string[];
  gradeOptions?: string[];
  genderOptions?: string[];
  waitlistPolicies?: string[];
  locationOptions?: string[];
};

type ExistingProgram = {
  id?: string;
  name?: string;
  description?: string;
  campType?: string;
  location?: string;
  gradeMin?: string;
  gradeMax?: string;
  genderEligibility?: string;
  priceCentsPerSession?: number;
  siblingDiscountPercent?: number;
  multiSessionDiscountPercent?: number;
  capacityPerSession?: number;
  waitlistPolicy?: string;
  waiverText?: string;
  packingList?: string;
  sessions?: Array<{
    id?: string;
    startDate?: string;
    endDate?: string;
    dailyStartTime?: string;
    dailyEndTime?: string;
  }>;
};

type EditorResponse = {
  program?: ExistingProgram;
  sessions?: ExistingProgram['sessions'];
  defaults?: EditorDefaults;
  ingestHealth?: { state?: string; lastRefreshAt?: string; sourceSystem?: string };
};

type FormState = {
  name: string;
  description: string;
  campType: string;
  location: string;
  gradeMin: string;
  gradeMax: string;
  genderEligibility: string;
  sessionStartDate: string;
  sessionEndDate: string;
  dailyStartTime: string;
  dailyEndTime: string;
  priceCentsPerSession: string;
  siblingDiscountPercent: string;
  multiSessionDiscountPercent: string;
  capacityPerSession: string;
  waitlistPolicy: string;
  waiverText: string;
  packingList: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  campType: '',
  location: '',
  gradeMin: '',
  gradeMax: '',
  genderEligibility: '',
  sessionStartDate: '',
  sessionEndDate: '',
  dailyStartTime: '',
  dailyEndTime: '',
  priceCentsPerSession: '',
  siblingDiscountPercent: '',
  multiSessionDiscountPercent: '',
  capacityPerSession: '',
  waitlistPolicy: '',
  waiverText: '',
  packingList: '',
};

/* ── Loading skeleton ───────────────────────────────────────────────── */
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-40 animate-pulse rounded bg-gray-100" />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(280px,1fr)]">
        <div className="space-y-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
        <div className="h-80 animate-pulse rounded-xl bg-gray-100" />
      </div>
    </div>
  );
}

/* ── Error banner ───────────────────────────────────────────────────── */
function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="text-sm font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

/* ── Form section card wrapper ──────────────────────────────────────── */
function FormSection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-xl border bg-white p-6 shadow-sm"
      style={{ borderColor: BORDER }}
    >
      <div className="mb-4 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold" style={{ color: TEXT }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ── Form field helpers ─────────────────────────────────────────────── */
function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em]"
      style={{ color: TEXT_SECONDARY }}
    >
      {children}
    </label>
  );
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  if (type === 'date') {
    return (
      <TailwindDateField
        id={id}
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
      />
    );
  }

  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
      style={{
        borderColor: BORDER,
        color: TEXT,
        '--tw-ring-color': PRIMARY,
      } as React.CSSProperties}
    />
  );
}

function SelectInput({
  id,
  value,
  onChange,
  options,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
      style={{
        borderColor: BORDER,
        color: TEXT,
        '--tw-ring-color': PRIMARY,
      } as React.CSSProperties}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

/* ── Checklist item ─────────────────────────────────────────────────── */
function ChecklistItem({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm" style={{ color: TEXT }}>
      {done ? (
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: AQUAMARINE }} />
      ) : (
        <Circle className="h-4 w-4 flex-shrink-0" style={{ color: BORDER }} />
      )}
      <span style={{ color: done ? TEXT : TEXT_SECONDARY }}>{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Builder Page
   ═══════════════════════════════════════════════════════════════════════ */

export function SchoolCampsBuilderPage() {
  const { campId } = useParams<{ campId?: string }>();
  const isEditing = Boolean(campId);
  const navigate = useNavigate();

  const { data: rawData, isLoading, isError, error } = useGetProgramEditorQuery(
    campId ? { programId: campId } : undefined,
  );
  const [createProgram, { isLoading: isCreating }] = useCreateProgramMutation();
  const [updateProgram, { isLoading: isUpdating }] = useUpdateProgramMutation();
  const [publishProgram] = usePublishProgramMutation();

  const editorData = mapCampProgramEditorResponse(rawData) as EditorResponse;
  const defaults = editorData.defaults ?? {};
  const existingProgram = editorData.program;

  const initialForm = useMemo<FormState>(() => {
    if (!existingProgram) return EMPTY_FORM;
    const firstSession = existingProgram.sessions?.[0];
    return {
      name: existingProgram.name ?? '',
      description: existingProgram.description ?? '',
      campType: existingProgram.campType ?? '',
      location: existingProgram.location ?? '',
      gradeMin: existingProgram.gradeMin ?? '',
      gradeMax: existingProgram.gradeMax ?? '',
      genderEligibility: existingProgram.genderEligibility ?? '',
      sessionStartDate: firstSession?.startDate ?? '',
      sessionEndDate: firstSession?.endDate ?? '',
      dailyStartTime: firstSession?.dailyStartTime ?? '',
      dailyEndTime: firstSession?.dailyEndTime ?? '',
      priceCentsPerSession: existingProgram.priceCentsPerSession != null
        ? String(existingProgram.priceCentsPerSession / 100)
        : '',
      siblingDiscountPercent: existingProgram.siblingDiscountPercent != null
        ? String(existingProgram.siblingDiscountPercent)
        : '',
      multiSessionDiscountPercent: existingProgram.multiSessionDiscountPercent != null
        ? String(existingProgram.multiSessionDiscountPercent)
        : '',
      capacityPerSession: existingProgram.capacityPerSession != null
        ? String(existingProgram.capacityPerSession)
        : '',
      waitlistPolicy: existingProgram.waitlistPolicy ?? '',
      waiverText: existingProgram.waiverText ?? '',
      packingList: existingProgram.packingList ?? '',
    };
  }, [existingProgram]);

  const [form, setForm] = useState<FormState>(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const setField = useCallback(
    <K extends keyof FormState>(field: K) =>
      (value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [field]: value })),
    [],
  );

  /* ── Publish readiness checks ─────────────────────────────────────── */
  const hasName = form.name.trim().length > 0;
  const hasCampType = form.campType.trim().length > 0;
  const hasSession = form.sessionStartDate.length > 0 && form.sessionEndDate.length > 0;
  const hasTimes = form.dailyStartTime.length > 0 && form.dailyEndTime.length > 0;
  const hasPrice = form.priceCentsPerSession.length > 0 && Number(form.priceCentsPerSession) > 0;
  const hasCapacity = form.capacityPerSession.length > 0 && Number(form.capacityPerSession) > 0;
  const hasGrades = form.gradeMin.length > 0 && form.gradeMax.length > 0;
  const allReady = hasName && hasCampType && hasSession && hasTimes && hasPrice && hasCapacity && hasGrades;

  /* ── Submission ───────────────────────────────────────────────────── */
  const buildPayload = useCallback((): Record<string, unknown> => ({
    ...(campId ? { programId: campId } : {}),
    program: {
      name: form.name,
      description: form.description || null,
      campType: form.campType,
      location: form.location || null,
      minGrade: form.gradeMin,
      maxGrade: form.gradeMax,
      genderEligibility: form.genderEligibility,
      pricePerSessionCents: Math.round(Number(form.priceCentsPerSession) * 100),
      siblingDiscountCents: Math.round(
        (Math.round(Number(form.priceCentsPerSession) * 100) * (Number(form.siblingDiscountPercent) || 0)) / 100,
      ) || 0,
      multiSessionDiscountPct: Number(form.multiSessionDiscountPercent) || 0,
      registrationOpen: form.sessionStartDate,
      registrationClose: form.sessionEndDate,
      cancelFullRefundBefore: form.sessionStartDate || null,
      cancelPartialRefundBefore: form.sessionEndDate || null,
      cancelPartialRefundPct: 0,
      waiverText: form.waiverText || null,
      packingList: form.packingList || null,
      status: 'draft',
      sourceSystem: 'ystnj_native',
      externalSourceKey: null,
      sourceSnapshotDate: null,
    },
    sessions: [
      {
        ...(existingProgram?.sessions?.[0]?.id ? { id: existingProgram.sessions[0].id } : {}),
        name: `${form.name || 'Camp'} Session`,
        startDate: form.sessionStartDate,
        endDate: form.sessionEndDate,
        dailyStartTime: form.dailyStartTime,
        dailyEndTime: form.dailyEndTime,
        capacity: Number(form.capacityPerSession) || 0,
        enrolledCount: 0,
        waitlistCount: 0,
        sourceSystem: 'ystnj_native',
        externalSourceKey: null,
        sourceSnapshotDate: null,
      },
    ],
  }), [campId, existingProgram?.sessions, form]);

  const handleSaveDraft = useCallback(async () => {
    const payload = buildPayload();
    try {
      if (isEditing) {
        await updateProgram({
          programId: payload.programId,
          updates: payload.program,
          sessions: payload.sessions,
        }).unwrap();
      } else {
        await createProgram({
          program: payload.program,
          sessions: payload.sessions,
        }).unwrap();
      }
      navigate('/school/camps');
    } catch {
      // mutation error is surfaced via RTK state
    }
  }, [buildPayload, isEditing, updateProgram, createProgram, navigate]);

  const handlePublish = useCallback(async () => {
    const payload = buildPayload();
    try {
      let targetProgramId = campId ?? '';
      if (isEditing) {
        await updateProgram({
          programId: payload.programId,
          updates: payload.program,
          sessions: payload.sessions,
        }).unwrap();
      } else {
        const created = await createProgram({
          program: payload.program,
          sessions: payload.sessions,
        }).unwrap() as { program?: { id?: string } };
        targetProgramId = String(created.program?.id ?? '').trim();
      }
      if (!targetProgramId) {
        throw new Error('Camp publish target id is missing.');
      }
      await publishProgram({ programId: targetProgramId }).unwrap();
      navigate('/school/camps');
    } catch {
      // mutation error is surfaced via RTK state
    }
  }, [buildPayload, campId, createProgram, isEditing, navigate, publishProgram, updateProgram]);

  /* ── Select option builders ───────────────────────────────────────── */
  const campTypeOptions = (defaults.campTypes ?? ['summer_day', 'specialty', 'sports', 'academic', 'other']).map(
    (v) => ({ value: v, label: v.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }),
  );

  const gradeOptions = (defaults.gradeOptions ?? ['Pre-K', 'K', '1', '2', '3', '4', '5', '6', '7', '8']).map(
    (v) => ({ value: v, label: `Grade ${v}` }),
  );

  const genderOptions = (defaults.genderOptions ?? ['all', 'boys', 'girls']).map(
    (v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }),
  );

  const waitlistOptions = (defaults.waitlistPolicies ?? ['auto-promote', 'manual-review', 'disabled']).map(
    (v) => ({ value: v, label: v.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }),
  );

  if (isLoading) {
    return (
      <main
        data-testid="school-camps-builder"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="school-camps-builder"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <NavLink
            to="/school/camps"
            className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
            style={{ color: PRIMARY }}
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </NavLink>
          <ErrorBanner
            title="Unable to load camp editor"
            detail={readDomainRouterErrorMessage(
              error,
              'The camps editor backend is not yet available.'
            )}
          />
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="school-camps-builder"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-camps-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <div className="mx-auto max-w-[1440px] space-y-6">
        {/* ── Back link ──────────────────────────────────────────── */}
        <NavLink
          to="/school/camps"
          className="inline-flex items-center gap-1 text-sm font-medium transition-colors hover:underline"
          style={{ color: PRIMARY }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </NavLink>

        {/* ── Page header ────────────────────────────────────────── */}
        <div data-testid="school-camps-builder-header">
          <p
            className="text-[12px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: PRIMARY }}
          >
            Camps
          </p>
          <h1 className="text-[24px] font-semibold" style={{ color: TEXT }}>
            {isEditing ? 'Edit Camp' : 'Create New Camp'}
          </h1>
        </div>

        <ModuleSectionNav
          items={SCHOOL_CAMPS_SECTION_ITEMS}
          activeId="new"
          ariaLabel="Camp operations navigation"
          mobileMenuTitle="Camp operations"
          testIds={{
            root: "school-camps-section-nav",
            itemPrefix: "school-camps-section-nav-item",
            mobileTrigger: "school-camps-section-nav-mobile-trigger",
            mobileDialog: "school-camps-section-nav-mobile-dialog",
            mobileItemPrefix: "school-camps-section-nav-mobile-item",
          }}
        />

        {/* ── Two-column layout ──────────────────────────────────── */}
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(280px,1fr)]">
          {/* Left column: form sections */}
          <div className="space-y-6" data-testid="school-camps-builder-form">
            {/* Basics */}
            <FormSection
              icon={<Tag className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Basics"
            >
              <div className="space-y-4">
                <div>
                  <FieldLabel htmlFor="camp-name">Camp Name *</FieldLabel>
                  <TextInput
                    id="camp-name"
                    value={form.name}
                    onChange={setField('name')}
                    placeholder="e.g. Summer Discovery Week"
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="camp-description">Description</FieldLabel>
                  <textarea
                    id="camp-description"
                    value={form.description}
                    onChange={(e) => setField('description')(e.target.value)}
                    placeholder="Describe the camp program..."
                    rows={3}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      borderColor: BORDER,
                      color: TEXT,
                      '--tw-ring-color': PRIMARY,
                    } as React.CSSProperties}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="camp-type">Camp Type *</FieldLabel>
                    <SelectInput
                      id="camp-type"
                      value={form.campType}
                      onChange={setField('campType')}
                      options={campTypeOptions}
                      placeholder="Select type..."
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="camp-location">Location</FieldLabel>
                    <TextInput
                      id="camp-location"
                      value={form.location}
                      onChange={setField('location')}
                      placeholder="e.g. Main Campus"
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Schedule */}
            <FormSection
              icon={<Calendar className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Schedule"
            >
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="session-start">Session Start Date *</FieldLabel>
                    <TextInput
                      id="session-start"
                      type="date"
                      value={form.sessionStartDate}
                      onChange={setField('sessionStartDate')}
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="session-end">Session End Date *</FieldLabel>
                    <TextInput
                      id="session-end"
                      type="date"
                      value={form.sessionEndDate}
                      onChange={setField('sessionEndDate')}
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="daily-start">Daily Start Time</FieldLabel>
                    <TextInput
                      id="daily-start"
                      type="time"
                      value={form.dailyStartTime}
                      onChange={setField('dailyStartTime')}
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="daily-end">Daily End Time</FieldLabel>
                    <TextInput
                      id="daily-end"
                      type="time"
                      value={form.dailyEndTime}
                      onChange={setField('dailyEndTime')}
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Eligibility */}
            <FormSection
              icon={<Users className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Eligibility"
            >
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="grade-min">Minimum Grade *</FieldLabel>
                    <SelectInput
                      id="grade-min"
                      value={form.gradeMin}
                      onChange={setField('gradeMin')}
                      options={gradeOptions}
                      placeholder="Select grade..."
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="grade-max">Maximum Grade *</FieldLabel>
                    <SelectInput
                      id="grade-max"
                      value={form.gradeMax}
                      onChange={setField('gradeMax')}
                      options={gradeOptions}
                      placeholder="Select grade..."
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel htmlFor="gender-eligibility">Gender Eligibility</FieldLabel>
                  <div className="flex flex-wrap gap-3">
                    {genderOptions.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm" style={{ color: TEXT }}>
                        <input
                          type="radio"
                          name="gender-eligibility"
                          value={opt.value}
                          checked={form.genderEligibility === opt.value}
                          onChange={(e) => setField('genderEligibility')(e.target.value)}
                          className="accent-[var(--primary)]"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Pricing */}
            <FormSection
              icon={<DollarSign className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Pricing"
            >
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <FieldLabel htmlFor="price-per-session">Price Per Session ($) *</FieldLabel>
                    <TextInput
                      id="price-per-session"
                      type="number"
                      value={form.priceCentsPerSession}
                      onChange={setField('priceCentsPerSession')}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="sibling-discount">Sibling Discount (%)</FieldLabel>
                    <TextInput
                      id="sibling-discount"
                      type="number"
                      value={form.siblingDiscountPercent}
                      onChange={setField('siblingDiscountPercent')}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="multi-session-discount">Multi-Session Discount (%)</FieldLabel>
                    <TextInput
                      id="multi-session-discount"
                      type="number"
                      value={form.multiSessionDiscountPercent}
                      onChange={setField('multiSessionDiscountPercent')}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Capacity */}
            <FormSection
              icon={<Layers className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Capacity"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel htmlFor="capacity">Capacity Per Session *</FieldLabel>
                  <TextInput
                    id="capacity"
                    type="number"
                    value={form.capacityPerSession}
                    onChange={setField('capacityPerSession')}
                    placeholder="e.g. 30"
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="waitlist-policy">Waitlist Policy</FieldLabel>
                  <SelectInput
                    id="waitlist-policy"
                    value={form.waitlistPolicy}
                    onChange={setField('waitlistPolicy')}
                    options={waitlistOptions}
                    placeholder="Select policy..."
                  />
                </div>
              </div>
            </FormSection>

            {/* Waivers & Packing */}
            <FormSection
              icon={<FileText className="h-4 w-4" style={{ color: PRIMARY }} />}
              title="Waivers & Packing List"
            >
              <div className="space-y-4">
                <div>
                  <FieldLabel htmlFor="waiver-text">Waiver / Liability Text</FieldLabel>
                  <textarea
                    id="waiver-text"
                    value={form.waiverText}
                    onChange={(e) => setField('waiverText')(e.target.value)}
                    placeholder="Enter waiver or liability acknowledgement text..."
                    rows={4}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      borderColor: BORDER,
                      color: TEXT,
                      '--tw-ring-color': PRIMARY,
                    } as React.CSSProperties}
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="packing-list">Packing List</FieldLabel>
                  <textarea
                    id="packing-list"
                    value={form.packingList}
                    onChange={(e) => setField('packingList')(e.target.value)}
                    placeholder="List items campers should bring..."
                    rows={3}
                    className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:ring-2"
                    style={{
                      borderColor: BORDER,
                      color: TEXT,
                      '--tw-ring-color': PRIMARY,
                    } as React.CSSProperties}
                  />
                </div>
              </div>
            </FormSection>
          </div>

          {/* Right column: summary/checklist */}
          <div className="space-y-6" data-testid="school-camps-builder-sidebar">
            <div
              className="sticky top-6 space-y-6 rounded-xl border bg-white p-6 shadow-sm"
              style={{ borderColor: BORDER }}
            >
              <div>
                <h3 className="text-sm font-semibold" style={{ color: TEXT }}>
                  Publish Readiness
                </h3>
                <p className="mt-1 text-xs" style={{ color: TEXT_SECONDARY }}>
                  Complete all required fields before publishing.
                </p>
              </div>

              <div className="space-y-2.5">
                <ChecklistItem label="Camp name provided" done={hasName} />
                <ChecklistItem label="Camp type selected" done={hasCampType} />
                <ChecklistItem label="Session dates defined" done={hasSession} />
                <ChecklistItem label="Grade range set" done={hasGrades} />
                <ChecklistItem label="Pricing configured" done={hasPrice} />
                <ChecklistItem label="Capacity set" done={hasCapacity} />
              </div>

              <div className="space-y-2.5 border-t pt-4" style={{ borderColor: BORDER }}>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={!allReady || isCreating || isUpdating}
                  className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: allReady ? PRIMARY : CORAL }}
                >
                  <Send className="h-4 w-4" />
                  {isEditing ? 'Update & Publish' : 'Create & Publish'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={!hasName || isCreating || isUpdating}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 disabled:opacity-50"
                  style={{ borderColor: BORDER, color: TEXT }}
                >
                  <Save className="h-4 w-4" />
                  Save Draft
                </button>
              </div>

              {form.name ? (
                <div className="border-t pt-4" style={{ borderColor: BORDER }}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: TEXT_SECONDARY }}>
                    Preview
                  </p>
                  <p className="mt-2 text-sm font-medium" style={{ color: TEXT }}>{form.name}</p>
                  {form.campType ? (
                    <p className="mt-0.5 text-xs" style={{ color: TEXT_SECONDARY }}>
                      {form.campType.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      {form.location ? ` \u00b7 ${form.location}` : ''}
                    </p>
                  ) : null}
                  {hasGrades ? (
                    <p className="mt-0.5 text-xs" style={{ color: TEXT_SECONDARY }}>
                      Grades {form.gradeMin} - {form.gradeMax}
                    </p>
                  ) : null}
                  {hasPrice ? (
                    <p className="mt-0.5 text-xs" style={{ color: TEXT_SECONDARY }}>
                      ${Number(form.priceCentsPerSession).toFixed(2)} / session
                    </p>
                  ) : null}
                  {hasCapacity ? (
                    <p className="mt-0.5 text-xs" style={{ color: TEXT_SECONDARY }}>
                      Capacity: {form.capacityPerSession} per session
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
